// The night behind both screens (Night Sky Construction): the weekday's
// gradient two screens tall, play above and home below, the Milky Way where
// the day has one, three depth layers of background stars, the atlas
// constellations and the horizon hills on the home half, and the layer the
// animated bits appear in. It is mounted once for the whole app; the scene
// host rests it on a half and pans it between them, and the motion loop
// drifts it sideways. With `chart` (the Horoscope) the home half is redrawn
// as a planisphere: the same stars as chart dots over chart ink, the figures
// ringed, the grid laid in, the hills gone (sky.css, Planisphere).
import { useEffect, useLayoutEffect, useMemo, useRef, type Ref } from 'react';
import type { Weekday } from '../../systems/api/types';
import { ambientSettings } from './ambient/ambientSettings';
import { AtlasLayer } from './AtlasLayer';
import { Planisphere } from './Planisphere';
import { activeSky, SkyMotion, type Half } from './skyMotion';
import { StarLayer } from './StarLayer';
import { useViewSize } from './useViewSize';
import { starSize } from './viewport';
import { backgroundStars, HALF_HEIGHT, WEEKDAY_SKIES } from './weekdaySkies';
import './sky.css';

interface Props {
  ref?: Ref<HTMLDivElement>;
  /** Null until the night is known: the sky fades in then, rather than flashing another day. */
  weekday: Weekday | null;
  /** The half the sky rests on. */
  at: Half;
  /** The half the animated bits belong to. */
  half: Half;
  drifting: boolean;
  resting: boolean;
  figuresShown: boolean;
  figuresFadeMs: number;
  /** The home half drawn as a planisphere (the Horoscope). */
  chart: boolean;
}

export function SkyStage({ ref, weekday, at, half, drifting, resting, figuresShown, figuresFadeMs, chart }: Props) {
  const day = weekday ?? 'thursday';
  const sky = WEEKDAY_SKIES[day];
  const stars = useMemo(() => backgroundStars(day), [day]);
  const view = useViewSize();
  const scale = view.height / HALF_HEIGHT;
  const motion = useMemo(() => new SkyMotion(), []);
  const starRoot = useRef<HTMLDivElement>(null);
  const ambientLayer = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    motion.view = { width: view.width, height: view.height, scale };
    motion.relayout();
  }, [motion, view.width, view.height, scale]);

  useLayoutEffect(() => {
    motion.inputs = { weekday: day, half, drifting, resting };
  });

  useEffect(() => {
    motion.stars = stars;
    motion.reschedule();
  }, [motion, stars]);

  useEffect(() => {
    const detach = motion.attachAmbient(ambientLayer.current!, starRoot.current!);
    const unsubscribe = ambientSettings.subscribe(() => motion.reschedule());
    motion.start();
    activeSky.current = motion;
    return () => {
      activeSky.current = null;
      motion.stop();
      unsubscribe();
      detach();
    };
  }, [motion]);

  const visibleWidth = view.width / scale;
  const size = starSize(view.width, view.height);
  const milkyWay = sky.milkyWay > 0 && <div className="sky-milky-way" style={{ opacity: sky.milkyWay }} />;

  return (
    <div
      ref={ref}
      className="sky-stage"
      data-at={at}
      data-ready={weekday !== null}
      data-chart={chart}
      style={{ height: 2 * view.height, background: `linear-gradient(to bottom, ${sky.top} 0%, ${sky.mid} 50%, ${sky.bottom} 100%)` }}
      aria-hidden="true"
    >
      <div className="sky-half sky-half-play">{milkyWay}</div>
      <div className="sky-half sky-half-home">
        {milkyWay}
        <div className="sky-chart">
          <Planisphere width={view.width} height={view.height} weekday={day} milkyWay={sky.milkyWay} />
        </div>
      </div>
      <div ref={starRoot} className="sky-depths">
        {([0, 1, 2] as const).map((depth) => (
          <StarLayer key={`${day}-${depth}`} motion={motion} depth={depth} stars={stars} scale={scale} visibleWidth={visibleWidth} size={size} />
        ))}
      </div>
      <AtlasLayer motion={motion} scale={scale} visibleWidth={visibleWidth} shown={figuresShown} fadeMs={figuresFadeMs} />
      <div ref={ambientLayer} className="sky-ambient" />
      <div className="sky-half sky-half-home">
        {sky.horizonWarmth > 0 && <div className="sky-horizon-glow" style={{ opacity: sky.horizonWarmth }} />}
        <div className="sky-horizon" />
      </div>
    </div>
  );
}
